import React, { useState, useEffect } from 'react';
import { base44 } from '@/api/base44Client';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, ShieldOff, Mail, Save, Eye, X, ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { useAuth } from '@/lib/AuthContext';

const QUILL_MODULES = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ['bold', 'italic', 'underline'],
    [{ color: [] }, { background: [] }],
    [{ align: [] }, { list: 'ordered' }, { list: 'bullet' }],
    ['link', 'clean'],
  ],
};

const PLACEHOLDERS = [
  { tag: '{{first_name}}', desc: "Customer's first name" },
  { tag: '{{experience_label}}', desc: 'Experience name e.g. Adult Sim Racing Experience' },
  { tag: '{{session_date}}', desc: 'Session date (already includes " on ", or blank)' },
  { tag: '{{feedback_url}}', desc: 'Link to the feedback form' },
  { tag: '{{site_url}}', desc: 'motionreality.co.uk' },
];

export default function AdminEmailTemplates() {
  const { user: currentUser, isLoadingAuth: authLoading } = useAuth();
  const [selectedId, setSelectedId] = useState(null);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);
  const [preview, setPreview] = useState(false);
  const [mode, setMode] = useState('visual');
  const qc = useQueryClient();

  const { data: templates = [], isLoading } = useQuery({
    queryKey: ['email-templates'],
    queryFn: () => base44.entities.EmailTemplate.list('name', 50),
    enabled: currentUser?.role === 'admin',
  });

  useEffect(() => {
    if (templates.length && !selectedId) setSelectedId(templates[0].id);
  }, [templates, selectedId]);

  const selected = templates.find((t) => t.id === selectedId);

  useEffect(() => {
    if (selected) {
      setSubject(selected.subject || '');
      setBody(selected.body_html || '');
      setMsg(null);
    }
  }, [selectedId]); // eslint-disable-line react-hooks/exhaustive-deps

  if (authLoading) {
    return (
      <div className="min-h-screen bg-[#0d0d0f] pt-16 flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
      </div>
    );
  }

  if (!currentUser || currentUser.role !== 'admin') {
    return (
      <div className="min-h-screen bg-[#0d0d0f] pt-16 flex items-center justify-center">
        <div className="text-center">
          <ShieldOff className="w-12 h-12 text-white/10 mx-auto mb-4" />
          <p className="text-white/40 text-sm font-bold tracking-widest">ACCESS DENIED</p>
          <Link to="/admin" className="inline-block mt-5 text-primary text-xs font-bold tracking-widest hover:underline">SIGN IN →</Link>
        </div>
      </div>
    );
  }

  const save = async () => {
    if (!selected) return;
    setSaving(true);
    setMsg(null);
    try {
      await base44.entities.EmailTemplate.update(selected.id, { subject, body_html: body });
      qc.invalidateQueries({ queryKey: ['email-templates'] });
      setMsg('Saved — this template is now live.');
    } catch (e) {
      setMsg('Error: ' + e.message);
    }
    setSaving(false);
  };

  const previewHtml = (body || '')
    .replaceAll('{{first_name}}', 'Alex')
    .replaceAll('{{experience_label}}', 'Adult Sim Racing Experience')
    .replaceAll('{{session_date}}', ' on 17/08/2026')
    .replaceAll('{{feedback_url}}', 'https://www.motionreality.co.uk/feedback')
    .replaceAll('{{site_url}}', 'https://www.motionreality.co.uk');

  return (
    <div className="min-h-screen bg-[#0d0d0f] pt-16">
      <header className="py-12 px-6 lg:px-10 border-b border-white/5">
        <div className="max-w-6xl mx-auto">
          <Link to="/admin/dashboard" className="inline-flex items-center gap-2 text-white/30 hover:text-white text-xs font-bold tracking-widest mb-4 transition-colors">
            <ArrowLeft className="w-4 h-4" /> BACK TO DASHBOARD
          </Link>
          <p className="text-primary text-xs font-bold tracking-widest mb-2">MARKETING &amp; COMMS</p>
          <h1 className="text-white font-black text-2xl sm:text-3xl md:text-4xl uppercase">TRANSACTIONAL EMAILS</h1>
        </div>
      </header>

      <section className="px-6 lg:px-10 py-8 max-w-6xl mx-auto">
        {/* Order-confirmation email lives in the Invoice & Email Design page */}
        <Link
          to="/admin/invoice-design"
          className="flex flex-col sm:flex-row sm:items-center gap-3 border border-primary/20 bg-primary/[0.03] hover:bg-primary/[0.06] p-5 mb-8 transition-colors group"
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 border border-primary/30 bg-primary/10 flex items-center justify-center shrink-0">
              <Mail className="w-5 h-5 text-primary" />
            </div>
            <div>
              <p className="text-primary text-[10px] font-bold tracking-widest mb-0.5">ORDER-CONFIRMATION EMAIL</p>
              <p className="text-white text-sm font-bold">Invoice &amp; Email Design</p>
              <p className="text-white/40 text-xs mt-0.5">The order-confirmation email (with the VAT invoice PDF) is designed on the Invoice &amp; Email Design page.</p>
            </div>
          </div>
          <span className="text-primary text-xs font-bold tracking-widest sm:ml-auto group-hover:translate-x-1 transition-transform">OPEN →</span>
        </Link>

        {isLoading ? (
          <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
        ) : templates.length === 0 ? (
          <div className="text-center py-20 border border-white/5 bg-[#111115]">
            <Mail className="w-10 h-10 text-white/10 mx-auto mb-4" />
            <p className="text-white/30 text-sm">No email templates configured yet.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-6">
            {/* template list */}
            <div className="space-y-2">
              {templates.map((t) => (
                <button
                  key={t.id}
                  onClick={() => setSelectedId(t.id)}
                  className={`w-full text-left border p-4 transition-colors ${
                    selectedId === t.id ? 'border-primary/50 bg-primary/5' : 'border-white/5 bg-[#111115] hover:border-white/15'
                  }`}
                >
                  <p className="text-white text-sm font-bold">{t.name}</p>
                  <p className="text-white/30 text-[10px] mt-1 font-mono">{t.key}</p>
                </button>
              ))}
            </div>

            {/* editor */}
            {selected && (
              <div className="space-y-5">
                <div className="bg-[#111115] border border-white/5 p-6">
                  <p className="text-white/40 text-xs">{selected.description}</p>
                </div>

                <div>
                  <label className="block text-white/40 text-xs font-bold tracking-widest mb-2">SUBJECT</label>
                  <input
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                    className="w-full bg-[#111115] border border-white/10 focus:border-primary text-white text-sm px-4 py-3 outline-none"
                  />
                </div>

                <div>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-1.5">
                      <label className="text-white/40 text-xs font-bold tracking-widest mr-2">BODY</label>
                      <button
                        onClick={() => setMode('visual')}
                        className={`text-[10px] font-bold tracking-widest px-3 py-1.5 transition-colors ${mode === 'visual' ? 'bg-primary border border-primary text-white' : 'border border-white/10 text-white/50 hover:text-white'}`}
                      >
                        VISUAL
                      </button>
                      <button
                        onClick={() => setMode('source')}
                        className={`text-[10px] font-bold tracking-widest px-3 py-1.5 transition-colors ${mode === 'source' ? 'bg-primary border border-primary text-white' : 'border border-white/10 text-white/50 hover:text-white'}`}
                      >
                        HTML
                      </button>
                    </div>
                    <button
                      onClick={() => setPreview(true)}
                      className="inline-flex items-center gap-1.5 text-xs font-bold tracking-widest text-primary border border-primary/30 bg-primary/10 hover:bg-primary hover:text-white px-3 py-1.5 transition-colors"
                    >
                      <Eye className="w-3.5 h-3.5" /> PREVIEW
                    </button>
                  </div>
                  {mode === 'visual' ? (
                    <div className="bg-white border border-white/10 email-quill">
                      <ReactQuill theme="snow" value={body} onChange={setBody} modules={QUILL_MODULES} />
                    </div>
                  ) : (
                    <textarea
                      rows={18}
                      value={body}
                      onChange={(e) => setBody(e.target.value)}
                      className="w-full bg-[#111115] border border-white/10 focus:border-primary text-white/80 text-xs font-mono px-4 py-3 outline-none resize-y leading-relaxed"
                    />
                  )}
                  <p className="text-white/30 text-[10px] mt-2">Tip: use VISUAL to edit like a document, or HTML for direct control. Placeholders like {"{{first_name}}"} are plain text and can be typed in either mode.</p>
                </div>

                <div className="bg-[#111115] border border-white/5 p-5">
                  <p className="text-white/40 text-[10px] font-bold tracking-widest mb-3">AVAILABLE PLACEHOLDERS</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {PLACEHOLDERS.map((p) => (
                      <div key={p.tag} className="flex items-center gap-3">
                        <code className="text-primary font-mono text-xs">{p.tag}</code>
                        <span className="text-white/40 text-xs">{p.desc}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {msg && <p className="text-sm text-emerald-400">{msg}</p>}

                <button
                  onClick={save}
                  disabled={saving}
                  className="inline-flex items-center gap-2 bg-primary/20 border border-primary/40 hover:bg-primary hover:border-primary text-white font-bold text-sm tracking-widest px-6 py-3 transition-colors disabled:opacity-50"
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} SAVE TEMPLATE
                </button>
              </div>
            )}
          </div>
        )}
      </section>

      {preview && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={() => setPreview(false)}>
          <div className="bg-[#111115] border border-white/10 max-w-3xl w-full h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-3 border-b border-white/10">
              <p className="text-white font-bold text-sm">PREVIEW (sample data)</p>
              <button onClick={() => setPreview(false)} className="text-white/40 hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <div className="flex-1 bg-white">
              <iframe srcDoc={previewHtml} title="email-preview" className="w-full h-full border-0" />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}